<script setup lang="ts">
import { isProxy, toRaw } from 'vue';
import Multiselect from 'vue-multiselect/src/Multiselect.vue';

const { $t } = useLocalization();

type AutocompleteItem = {
    id: string | number;
    [key: string]: any;
};

interface Props
{
    name?: string;
    modelValue?: any;
    limit?: number;
    disabled?: boolean;
    refresh?: boolean;
    label?: string;
    placeholder?: string;
    fetch?: (item: any) => Promise<AutocompleteItem | null>;
    search: (query: string, limit: number) => Promise<AutocompleteItem[]>;
    multiple?: boolean;
    closeOnSelect?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    name: '',
    modelValue: null,
    limit: 10,
    disabled: false,
    refresh: false,
    label: 'name',
    multiple: true,
    closeOnSelect: true
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: any): void;
}>();

const options = ref<AutocompleteItem[]>([]);
const model = ref<any>(null);
const multipleOptions = ref<any>([]);
const loading = ref<boolean>(false);
const track = ref<string>('id');
const value = ref<any>([]);
const elementsId = ref<any>([]);
const loadedIds = ref<number[]>([]);

const valueChanged = computed({
    get: () => value.value,
    set: (item: any) =>
    {
        value.value = item;
    }
});

const removeDuplicates = (arr: Array<number>): Array<number> =>
{
    return [...(new Set(arr))];
};

function normalizeIds(input: any): number[]
{
    if (input === null || input === undefined) return [];

    if (Array.isArray(input))
    {
        return removeDuplicates(
            input
                .map(x => Number(x))
                .filter(x => Number.isFinite(x) && x !== 0)
        );
    }

    if (Number.isInteger(input))
    {
        const id = Number(input);

        return id !== 0 ? [id] : [];
    }

    if (typeof input === 'string')
    {
        const parts = input
            .split(',')
            .map(x => Number(x.trim()))
            .filter(x => Number.isFinite(x) && x !== 0);

        return removeDuplicates(parts);
    }

    return [];
}

function normalizeSelectedItems(input: any): AutocompleteItem[]
{
    if (!Array.isArray(input)) return [];

    const items = input.filter(x => x && typeof x === 'object' && 'id' in x) as AutocompleteItem[];

    return items.length > 0 ? items : [];
}

function areArraysEqual(a: number[], b: number[]): boolean
{
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++)
    {
        if (a[i] !== b[i]) return false;
    }

    return true;
}

function formatOptionLabel(option: AutocompleteItem | null | undefined): string
{
    if (!option) return '';

    const labelKey = props.label;
    const labelValue = option?.[labelKey];

    if (labelKey === 'givenName')
    {
        const givenName = typeof labelValue === 'string' ? labelValue : '';
        const surname = typeof option?.surname === 'string' ? option.surname : '';

        return `${givenName} ${surname}`.trim();
    }

    return typeof labelValue === 'string' ? labelValue : String(labelValue ?? '');
}

const searchOptions = async (query: string): Promise<void> =>
{
    if (query)
    {
        options.value = await props.search(query, props.limit);
    }
};

const fetchModel = async (value: any): Promise<void> =>
{
    const selectedItems = normalizeSelectedItems(value);

    if (selectedItems.length > 0)
    {
        loadedIds.value = selectedItems
            .map(x => Number((x as any)?.[track.value]))
            .filter(x => Number.isFinite(x) && x !== 0);

        model.value = props.multiple ? selectedItems : (selectedItems[0] ?? null);
        valueChanged.value = props.multiple ? selectedItems : (selectedItems[0] ?? null);

        const existingIds = new Set(options.value.map((x: any) => x?.[track.value]));

        for (const item of selectedItems)
        {
            const itemId = item?.[track.value];

            if (itemId === undefined || itemId === null) continue;

            if (existingIds.has(itemId)) continue;

            options.value.push(item);
            existingIds.add(itemId);
        }

        return;
    }

    const ids = normalizeIds(value);

    if (areArraysEqual(ids, loadedIds.value))
    {
        return;
    }

    if (ids.length === 0)
    {
        loadedIds.value = [];
        model.value = props.multiple ? [] : null;
        valueChanged.value = props.multiple ? [] : null;

        return;
    }

    if (!props.fetch)
    {
        // Component can be used in "search-only" mode (no initial fetch by ids).
        loadedIds.value = [];
        model.value = props.multiple ? [] : null;
        valueChanged.value = props.multiple ? [] : null;

        return;
    }

    const fetched: any[] = [];

    for (const id of ids)
    {
        const result = await props.fetch(id);

        if (!result) continue;

        fetched.push(result);
    }

    if (props.multiple)
    {
        loadedIds.value = ids;
        model.value = fetched;
        valueChanged.value = fetched;

        const existingIds = new Set(options.value.map((x: any) => x?.[track.value]));

        for (const item of fetched)
        {
            const itemId = item?.[track.value];

            if (itemId === undefined || itemId === null) continue;

            if (existingIds.has(itemId)) continue;

            options.value.push(item);
            existingIds.add(itemId);
        }

        return;
    }

    const first = fetched[0] ?? null;

    loadedIds.value = first ? [Number((first as any)?.[track.value])] : [];
    model.value = first;
    valueChanged.value = first;

    if (first)
    {
        const firstId = (first as any)?.[track.value];

        if (!options.value.some((x: any) => x?.[track.value] === firstId))
        {
            options.value.push(first);
        }
    }
};

const onSearchChanged = async (query: string): Promise<void> =>
{
    loading.value = true;
    await searchOptions(query);
    loading.value = false;
};

const removeItems = (item: any): string =>
{
    if (isProxy(valueChanged.value))
    {
        const rawValueChanged = toRaw(valueChanged.value);

        if (Array.isArray(rawValueChanged))
        {
            multipleOptions.value = rawValueChanged.filter((el: any) => el.id != item[track.value]);
        }
        else
        {
            multipleOptions.value = multipleOptions.value.filter((el: any) => el.id != rawValueChanged[track.value]);
        }
    }

    elementsId.value = [];
    multipleOptions.value.forEach((item: any) =>
    {
        elementsId.value.push(item[track.value]);
    });

    elementsId.value = removeDuplicates(elementsId.value);

    return elementsId.value;
};

const isProxyRaw = (arr: any): string =>
{
    if (isProxy(arr))
    {
        const rawObjectOrArray = toRaw(arr);

        if (rawObjectOrArray.length > 0)
        {
            rawObjectOrArray.forEach((x: any) =>
            {
                multipleOptions.value.push(x);
            });

            return updateMultipleOptions();
        }
        else
        {
            multipleOptions.value.push(rawObjectOrArray);

            return updateMultipleOptions();
        }
    }
};

const updateMultipleOptions = (): string =>
{
    if (isProxy(valueChanged.value))
    {
        const rawValueChanged = toRaw(valueChanged.value);

        if (Array.isArray(rawValueChanged))
        {
            rawValueChanged.forEach((el: any) =>
            {
                multipleOptions.value.push(el);
            });
        }
        else
        {
            multipleOptions.value.push(rawValueChanged);
        }
    }

    multipleOptions.value.forEach((item: any) =>
    {
        elementsId.value.push(item[track.value]);
    });

    elementsId.value = removeDuplicates(elementsId.value);

    return elementsId.value;
};

const updateModel = (model: AutocompleteItem, remove: boolean = false): string | AutocompleteItem[] =>
{
    if (props.multiple)
    {
        if (remove)
        {
            const currentValue = Array.isArray(valueChanged.value) ? [...valueChanged.value] : [];
            const filtered = currentValue.filter((item: any) => item?.[track.value] !== model?.[track.value]);

            valueChanged.value = filtered;
            emit('update:modelValue', filtered);

            return filtered;
        }
        else
        {
            const currentValue = Array.isArray(valueChanged.value) ? [...valueChanged.value] : [];
            const exists = currentValue.some((item: any) => item?.[track.value] === model?.[track.value]);

            if (!exists)
            {
                const updated = [...currentValue, model];

                valueChanged.value = updated;
                emit('update:modelValue', updated);

                return updated;
            }

            emit('update:modelValue', currentValue);

            return currentValue;
        }
    }
    else
    {
        let result: string;

        if (remove)
        {
            result = removeItems(model);
        }
        else
        {
            result = model != null ? isProxyRaw(model) : null;
        }

        emit('update:modelValue', result);

        return result;
    }
};

const open = (): void =>
{
    if (props.refresh)
    {
        searchOptions('');
    }
};

const close = (): void =>
{
    if (props.refresh)
    {
        if (model.value == null)
        {
            options.value = [];
        }
        else
        {
            options.value = options.value.filter(x => x == model.value);
        }
    }
};

const select = (item: AutocompleteItem): void =>
{
    updateModel(item);
};

const remove = async (item: any): Promise<void> =>
{
    updateModel(item, true);
};

watch(
    () => props.modelValue,
    async (nextValue: any) =>
    {
        await fetchModel(nextValue);
    },
    { immediate: true, deep: true }
);
</script>

<template>
    <Multiselect
        v-model="valueChanged"
        :options="options"
        :name="name"
        :loading="loading"
        track-by="id"
        :label="label"
        :custom-label="formatOptionLabel"
        :placeholder="placeholder || $t('[[[wybierz...]]]')"
        :select-label="''"
        :selected-label="''"
        :deselect-label="''"
        :multiple="multiple"
        :searchable="true"
        :options-limit="limit"
        :internal-search="false"
        :clear-on-select="false"
        :close-on-select="closeOnSelect"
        :max-height="300"
        :show-no-results="true"
        :hide-selected="false"
        :disabled="disabled"
        @search-change="onSearchChanged"
        @open="open()"
        @close="close()"
        @select="select"
        @remove="remove"
    >
        <template #noOptions>
            {{ $t('[[[Lista jest pusta]]]') }}
        </template>
        <template #noResult>
            {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
        </template>
        <template #singleLabel="{ option }">
            <slot name="selected" :option="option" />
        </template>
        <template #option="{ option }">
            <slot name="option" :option="option" />
        </template>
        <template #selection="{ values }">
            <slot name="selection" :option="values" />
        </template>
    </Multiselect>
</template>

<style lang="scss" scoped>
.multiselect-badges {
    background: #eee;
    padding: 4px 8px;
    border-radius: 5px;
    margin-bottom: 8px;
}
</style>
